import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { updateBugo } from '@shared/api/bugo/bugo.controller';
import { Bugo } from '@shared/api/bugo/bugo.interface';
import { updateCashAccount } from '@shared/api/cashAccount/cashAccount.controller';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { updateFevent } from '@shared/api/fevent/fevent.controller';
import { feventHookUrl } from '@shared/api/fevent/fevent.hook';
import { FeventOne, FeventUpdate } from '@shared/api/fevent/fevent.interface';
import { feventStateSelectorFamily } from '@shared/api/fevent/fevent.selector';
import { updateMember } from '@shared/api/member/member.controller';
import {
  Member,
  MemberUpdate,
  ShowCashAccountType,
} from '@shared/api/member/member.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { NotificationInstance } from 'antd/es/notification/interface';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';

import { useTitleHook } from '../hooks/useTitleHook';

export const FeventCustomConfigPage: FC = function FeventCustomConfigPage() {
  useTitleHook('관리자용 커스텀 설정');
  const { bugoRole, assetRole } = useAuth();
  const { feventId } = useParams();
  const feventId_ = feventId ?? 'undefined';
  const feventState = useRecoilValue(feventStateSelectorFamily(feventId_));
  const queryClient = useQueryClient();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const [useCustomConfig, setUseCustomConfig] = useState(false);
  const [hideFlowerOrderLink, setHideFlowerOrderLink] = useState(false);
  const [emptyAccountMessage, setEmptyAccountMessage] = useState('');
  const [hiddenBugoShopOrder, setHiddenBugoShopOrder] = useState(false);
  const [useAcqMsg, setUseAcqMsg] = useState(false);
  const [accountExpose, setAccountExpose] = useState(false);
  const [kakaoShareTemplateId, setKakaoShareTemplateId] = useState('');
  const [shareDisplayName, setShareDisplayName] = useState('');

  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [footerImageFile, setFooterImageFile] = useState<File | null>(null);
  const [headerImagePreview, setHeaderImagePreview] = useState<string>('');
  const [footerImagePreview, setFooterImagePreview] = useState<string>('');

  const [ogImageId, setOgImageId] = useState<string | null>(null);
  const [ogImageFile, setOgImageFile] = useState<File | null>(null);
  const [ogImagePreview, setOgImagePreview] = useState('');

  const [saving, setSaving] = useState(false);

  const fevent = feventState.status === 'success' ? feventState.data : null;
  const feventIdRef = useRef<string | null>(null);

  const [headerImageId, setHeaderImageId] = useState<string>(
    fevent?.hardCustomConfig?.headerImage as unknown as string,
  );
  const [footerImageId, setFooterImageId] = useState<string>(
    fevent?.hardCustomConfig?.footerImage as unknown as string,
  );

  useAsync(async () => {
    if (!headerImageId) return;
    const { data: image } = await api_.get(`/admin/image/${headerImageId}`);
    setHeaderImagePreview(fileBucketUrl(image.url, 'raw'));
    if (!footerImageId) return;
    const { data: image_ } = await api_.get(`/admin/image/${footerImageId}`);
    setFooterImagePreview(fileBucketUrl(image_.url, 'raw'));
  }, [headerImageId]);

  useEffect(() => {
    if (!fevent || feventIdRef.current === fevent._id) return;
    feventIdRef.current = fevent._id;

    const config = fevent.hardCustomConfig;
    setOgImageId(
      typeof config?.ogImage === 'string' ? config.ogImage : config?.ogImage?._id ?? null,
    );
    setOgImageFile(null);
    setOgImagePreview('');
    setUseCustomConfig(config?.useCustomConfig ?? false);
    setHideFlowerOrderLink(config?.hideFlowerOrderLink ?? false);
    setEmptyAccountMessage(config?.emptyAccountMessage ?? '');
    setHiddenBugoShopOrder(fevent.hiddenBugoShopOrder ?? false);
    setKakaoShareTemplateId(fevent.kakaoShareTemplateId ?? '');
    setUseAcqMsg(fevent.useAcqMsg ?? false);
    setAccountExpose(fevent.accountExpose ?? false);
    setShareDisplayName(fevent.deceasedInfo?.shareDisplayName ?? '');
    setHeaderImageId(config?.headerImage as unknown as string);
    setFooterImageId(config?.footerImage as unknown as string);
  }, [fevent]);

  useEffect(() => {
    let active = true;
    setOgImagePreview('');
    if (ogImageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        if (active && typeof reader.result === 'string') setOgImagePreview(reader.result);
      };
      reader.readAsDataURL(ogImageFile);
      return () => {
        active = false;
        if (reader.readyState === FileReader.LOADING) reader.abort();
      };
    }
    if (!ogImageId) return;
    api_
      .get(`/admin/image/${ogImageId}`)
      .then(({ data: image }) => {
        if (active) setOgImagePreview(fileBucketUrl(image.url, 'raw'));
      })
      .catch(() => {
        if (active)
          notificateError(
            notificationInstance,
            '공유 이미지 미리보기를 불러오지 못했습니다.',
          );
      });
    return () => {
      active = false;
    };
  }, [ogImageId, ogImageFile, notificationInstance]);

  const handleImageSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (f: File | null) => void,
    setPreview: (s: string) => void,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = useCallback(async () => {
    if (!fevent) return;
    setSaving(true);
    try {
      let headerImageId = fevent.hardCustomConfig?.headerImage as unknown as string;
      let footerImageId = fevent.hardCustomConfig?.footerImage as unknown as string;

      if (headerImageFile) {
        const uploaded = await postImage(headerImageFile, assetRole());
        headerImageId = uploaded._id;
      }
      if (footerImageFile) {
        const uploaded = await postImage(footerImageFile, assetRole());
        footerImageId = uploaded._id;
      }

      const savedOgImageId = ogImageFile
        ? (await postImage(ogImageFile, assetRole()))._id
        : ogImageId;

      const hardCustomConfig: Record<string, unknown> = {
        ogImage: savedOgImageId,
        useCustomConfig,
        hideFlowerOrderLink,
        emptyAccountMessage: emptyAccountMessage || undefined,
      };

      if (headerImageId) {
        hardCustomConfig.headerImage = headerImageId;
      }
      if (footerImageId) {
        hardCustomConfig.footerImage = footerImageId;
      }

      const feventUpdatePayload: Record<string, unknown> = {
        _id: fevent._id,
        hardCustomConfig,
        hiddenBugoShopOrder,
        useAcqMsg,
        accountExpose,
        kakaoShareTemplateId: kakaoShareTemplateId || undefined,
        'deceasedInfo.shareDisplayName': shareDisplayName || null,
      };

      await updateFevent(feventUpdatePayload as FeventUpdate, bugoRole());

      setOgImageId(savedOgImageId);
      setOgImageFile(null);
      await queryClient.invalidateQueries(feventHookUrl(feventId_, bugoRole()));
      notificateSuccess(notificationInstance, '저장 완료');
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '저장 실패');
    } finally {
      setSaving(false);
    }
  }, [
    fevent,
    headerImageFile,
    footerImageFile,
    ogImageId,
    ogImageFile,
    useCustomConfig,
    hideFlowerOrderLink,
    emptyAccountMessage,
    hiddenBugoShopOrder,
    useAcqMsg,
    accountExpose,
    kakaoShareTemplateId,
    shareDisplayName,
    assetRole,
    bugoRole,
    queryClient,
    feventId_,
    notificationInstance,
  ]);

  const labelClass = 'text-sm font-semibold text-gray-700';
  const sectionClass = 'space-y-3 rounded-lg border border-gray-200 bg-white p-4';

  const render = useMemo(() => {
    if (feventState.status !== 'success' || !fevent) return null;

    return (
      <div className="space-y-4">
        {/* hardCustomConfig 섹션 */}
        <div className={sectionClass}>
          <h3 className="text-base font-bold text-gray-900">
            커스텀 설정 (hardCustomConfig)
          </h3>

          <div className="flex items-center justify-between">
            <label className={labelClass}>커스텀 설정 사용</label>
            <ToggleSwitch checked={useCustomConfig} onChange={setUseCustomConfig} />
          </div>

          <div
            className={`space-y-3 ${
              !useCustomConfig ? 'pointer-events-none opacity-40' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <label className={labelClass}>화환 주문 링크 숨김</label>
              <ToggleSwitch
                checked={hideFlowerOrderLink}
                onChange={setHideFlowerOrderLink}
                disabled={!useCustomConfig}
              />
            </div>

            <div>
              <label className={labelClass}>빈 계좌 메시지</label>
              <input
                type="text"
                value={emptyAccountMessage}
                onChange={(e) => setEmptyAccountMessage(e.target.value)}
                placeholder="계좌가 없을 때 표시할 메시지"
                disabled={!useCustomConfig}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className={labelClass}>헤더 이미지</label>
              <ImageUploadField
                preview={headerImagePreview}
                onFileSelect={(e) =>
                  handleImageSelect(e, setHeaderImageFile, setHeaderImagePreview)
                }
                onClear={() => {
                  setHeaderImageFile(null);
                  setHeaderImagePreview('');
                }}
                disabled={!useCustomConfig}
              />
            </div>

            <div>
              <label className={labelClass}>푸터 이미지</label>
              <ImageUploadField
                preview={footerImagePreview}
                onFileSelect={(e) =>
                  handleImageSelect(e, setFooterImageFile, setFooterImagePreview)
                }
                onClear={() => {
                  setFooterImageFile(null);
                  setFooterImagePreview('');
                }}
                disabled={!useCustomConfig}
              />
            </div>
            <div>
              <label className={labelClass}>링크 공유 이미지</label>
              <p className="mb-2 text-xs text-gray-500">
                이 행사에 연결된 부고장·상주 링크의 미리보기에 사용합니다. 등록하지 않으면
                기본 회사 로고가 표시됩니다.
              </p>
              <ImageUploadField
                preview={ogImagePreview}
                clearable={Boolean(ogImageId || ogImageFile)}
                accept="image/jpeg,image/png"
                disabled={!useCustomConfig || saving}
                onFileSelect={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (!['image/jpeg', 'image/png'].includes(file.type)) {
                    notificateError(
                      notificationInstance,
                      'JPG 또는 PNG 이미지를 선택해 주세요.',
                    );
                    e.target.value = '';
                    return;
                  }
                  setOgImageFile(file);
                  e.target.value = '';
                }}
                onClear={() => {
                  setOgImageId(null);
                  setOgImageFile(null);
                  setOgImagePreview('');
                }}
              />
              <p className="mt-1 text-xs text-gray-400">
                JPG·PNG 이미지 / 변경 후 이미 공유한 링크에는 이전 이미지가 잠시 표시될 수
                있습니다.
              </p>
            </div>
          </div>
        </div>

        {/* 공유 표시 설정 섹션 */}
        <div className={sectionClass}>
          <h3 className="text-base font-bold text-gray-900">공유 표시 설정</h3>

          <div>
            <label className={labelClass}>고인 공유 표시명 (shareDisplayName)</label>
            <p className="mb-1 text-xs text-gray-400">
              비워두면 기본값 &quot;故 [고인명]님&quot; 사용
            </p>
            <input
              type="text"
              value={shareDisplayName}
              onChange={(e) => setShareDisplayName(e.target.value)}
              placeholder={`故 ${fevent.deceasedInfo?.name ?? ''}님`}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* 기타 설정 섹션 */}
        <div className={sectionClass}>
          <h3 className="text-base font-bold text-gray-900">기타 설정</h3>

          <div className="flex items-center justify-between">
            <label className={labelClass}>부고 꽃 주문 숨김 (hiddenBugoShopOrder)</label>
            <ToggleSwitch
              checked={hiddenBugoShopOrder}
              onChange={setHiddenBugoShopOrder}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className={labelClass}>조의글 사용 (useAcqMsg)</label>
            <ToggleSwitch checked={useAcqMsg} onChange={setUseAcqMsg} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className={labelClass}>계좌 노출 [전체] (accountExpose)</label>
              <p className="text-xs text-gray-400">
                부고 페이지에서 계좌를 처음부터 펼침 + 공유 메시지에 계좌 포함
              </p>
            </div>
            <ToggleSwitch checked={accountExpose} onChange={setAccountExpose} />
          </div>

          <div>
            <label className={labelClass}>
              카카오 공유 템플릿 ID (kakaoShareTemplateId)
            </label>
            <input
              type="text"
              value={kakaoShareTemplateId}
              onChange={(e) => setKakaoShareTemplateId(e.target.value)}
              placeholder="카카오 공유 템플릿 ID"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* 저장 버튼 */}
        <div className="pb-4">
          <Button
            className="button-rectangle min-h-[48px] w-full bg-gray-800 text-white"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '저장 중...' : '저장'}
          </Button>
        </div>

        {/* 계좌 설정 섹션 */}
        <MemberCashAccountSection
          fevent={fevent}
          bugoRole={bugoRole}
          onRefresh={async () => {
            await queryClient.invalidateQueries(feventHookUrl(feventId_, bugoRole()));
          }}
          notificationInstance={notificationInstance}
        />
      </div>
    );
  }, [
    feventState.status,
    fevent,
    useCustomConfig,
    hideFlowerOrderLink,
    emptyAccountMessage,
    headerImagePreview,
    footerImagePreview,
    ogImagePreview,
    ogImageId,
    ogImageFile,
    hiddenBugoShopOrder,
    useAcqMsg,
    accountExpose,
    kakaoShareTemplateId,
    shareDisplayName,
    handleSave,
    saving,
    bugoRole,
    queryClient,
    feventId_,
    notificationInstance,
  ]);

  return (
    <div className="relative max-w-inherit">
      <div className="p-4">{render}</div>
    </div>
  );
};

const ToggleSwitch: FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
    } ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

type MemberLocalState = {
  showCashAccountType: ShowCashAccountType;
  subCashAccountIds: string[];
  shareDisplayCustom: {
    topString: string;
    bottomString: string;
  };
  accountExpose: boolean | undefined;
  bugoId?: string;
};

const MemberCashAccountSection: FC<{
  fevent: FeventOne;
  bugoRole: () => LowerRole;
  onRefresh: () => Promise<void>;
  notificationInstance: NotificationInstance | null;
}> = ({ fevent, bugoRole, onRefresh, notificationInstance }) => {
  const memberList = fevent.memberOrderList as Member<string, Bugo, CashAccount>[];
  const feventCashAccounts = (fevent.cashAccounts ?? []) as CashAccount[];
  const [loading, setLoading] = useState(false);

  const buildInitialState = useCallback((): Record<string, MemberLocalState> => {
    const state: Record<string, MemberLocalState> = {};
    for (const member of memberList) {
      const bugo = member.bugo as Bugo | undefined;
      state[member._id] = {
        showCashAccountType: member.showCashAccountType ?? ShowCashAccountType.Self,
        subCashAccountIds: (member.subCashAccounts ?? []).map((a) =>
          typeof a === 'object' ? a._id : a,
        ),
        shareDisplayCustom: {
          topString: member.shareDisplayCustom?.topString ?? '',
          bottomString: member.shareDisplayCustom?.bottomString ?? '',
        },
        accountExpose: bugo?.accountExpose,
        bugoId: bugo?._id,
      };
    }
    return state;
  }, [memberList]);

  const [localState, setLocalState] =
    useState<Record<string, MemberLocalState>>(buildInitialState);

  const memberIdsKey = memberList.map((m) => m._id).join(',');
  const prevMemberIdsKeyRef = useRef(memberIdsKey);

  useEffect(() => {
    if (prevMemberIdsKeyRef.current !== memberIdsKey) {
      prevMemberIdsKeyRef.current = memberIdsKey;
      setLocalState(buildInitialState());
    }
  }, [memberIdsKey, buildInitialState]);

  const hasChanges = useMemo(() => {
    for (const member of memberList) {
      const local = localState[member._id];
      if (!local) continue;
      const originalType = member.showCashAccountType ?? ShowCashAccountType.Self;
      if (local.showCashAccountType !== originalType) return true;
      const originalSubIds = (member.subCashAccounts ?? []).map((a) =>
        typeof a === 'object' ? a._id : a,
      );
      if (
        local.subCashAccountIds.length !== originalSubIds.length ||
        local.subCashAccountIds.some((id) => !originalSubIds.includes(id))
      )
        return true;
      const origTop = member.shareDisplayCustom?.topString ?? '';
      const origBottom = member.shareDisplayCustom?.bottomString ?? '';
      if (local.shareDisplayCustom.topString !== origTop) return true;
      if (local.shareDisplayCustom.bottomString !== origBottom) return true;
      const bugo = member.bugo as Bugo | undefined;
      if (local.accountExpose !== bugo?.accountExpose) return true;
    }
    return false;
  }, [localState, memberList]);

  const handleLocalShowTypeChange = (memberId: string, newType: ShowCashAccountType) => {
    setLocalState((prev) => {
      const existing = prev[memberId];
      if (!existing) return prev;
      return { ...prev, [memberId]: { ...existing, showCashAccountType: newType } };
    });
  };

  const handleLocalToggleSub = (memberId: string, accountId: string, add: boolean) => {
    setLocalState((prev) => {
      const existing = prev[memberId];
      if (!existing) return prev;
      const current = existing.subCashAccountIds;
      const next = add
        ? [...current, accountId]
        : current.filter((id) => id !== accountId);
      return { ...prev, [memberId]: { ...existing, subCashAccountIds: next } };
    });
  };

  const handleSaveAccountSettings = async () => {
    setLoading(true);
    try {
      const promises: Promise<unknown>[] = [];
      for (const member of memberList) {
        const local = localState[member._id];
        if (!local) continue;

        const originalType = member.showCashAccountType ?? ShowCashAccountType.Self;
        const originalSubIds = (member.subCashAccounts ?? []).map((a) =>
          typeof a === 'object' ? a._id : a,
        );

        const typeChanged = local.showCashAccountType !== originalType;
        const subChanged =
          local.subCashAccountIds.length !== originalSubIds.length ||
          local.subCashAccountIds.some((id) => !originalSubIds.includes(id));

        const origTop = member.shareDisplayCustom?.topString ?? '';
        const origBottom = member.shareDisplayCustom?.bottomString ?? '';
        const shareDisplayChanged =
          local.shareDisplayCustom.topString !== origTop ||
          local.shareDisplayCustom.bottomString !== origBottom;

        const bugo = member.bugo as Bugo | undefined;
        const accountExposeChanged = local.accountExpose !== bugo?.accountExpose;

        if (typeChanged || subChanged || shareDisplayChanged) {
          const update: MemberUpdate = { _id: member._id };
          if (typeChanged) update.showCashAccountType = local.showCashAccountType;
          if (subChanged) update.subCashAccounts = local.subCashAccountIds;
          if (shareDisplayChanged) {
            update.shareDisplayCustom = {
              topString: local.shareDisplayCustom.topString || undefined,
              bottomString: local.shareDisplayCustom.bottomString || undefined,
            };
          }
          promises.push(updateMember(update, bugoRole()));
        }

        if (accountExposeChanged && local.bugoId) {
          promises.push(
            updateBugo(
              { _id: local.bugoId, accountExpose: local.accountExpose ?? null } as any,
              bugoRole(),
            ),
          );
        }
      }
      await Promise.all(promises);
      await onRefresh();
      notificateSuccess(notificationInstance, '계좌 설정 저장 완료');
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '계좌 설정 저장 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCashAccount = async (cashAccountId: string) => {
    if (!window.confirm('이 계좌를 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'))
      return;
    setLoading(true);
    try {
      await api_.delete(`/${bugoRole()}/cash-account/${cashAccountId}`);
      await onRefresh();
      notificateSuccess(notificationInstance, '계좌 삭제 완료');
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '계좌 삭제 실패');
    } finally {
      setLoading(false);
    }
  };

  const sectionClass = 'space-y-3 rounded-lg border border-gray-200 bg-white p-4';
  const labelClass = 'text-sm font-semibold text-gray-700';

  const getAvailableAccountsForMember = (member: Member<string, Bugo, CashAccount>) => {
    const ownAccountId =
      member.cashAccount && typeof member.cashAccount === 'object'
        ? member.cashAccount._id
        : null;
    return feventCashAccounts.filter((a) => a._id !== ownAccountId);
  };

  const saveButton = (
    <div className="pt-2">
      <Button
        className={`button-rectangle min-h-[40px] w-full text-white ${
          hasChanges ? 'bg-blue-600' : 'bg-gray-300'
        }`}
        onClick={handleSaveAccountSettings}
        disabled={loading || !hasChanges}
      >
        {loading ? '저장 중...' : hasChanges ? '변경사항 저장' : '변경사항 없음'}
      </Button>
    </div>
  );

  return (
    <div className={sectionClass}>
      <h3 className="text-base font-bold text-gray-900">계좌 설정</h3>

      {saveButton}

      {memberList.map((member) => {
        const local = localState[member._id];
        if (!local) return null;
        const availableAccounts = getAvailableAccountsForMember(member);

        return (
          <div
            key={member._id}
            className="space-y-2 rounded-md border border-gray-100 bg-gray-50 p-3"
          >
            <div className="flex items-center justify-between">
              <span className={labelClass}>
                {member.shortName} ({member.relationType})
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">계좌 노출:</span>
                <select
                  value={local.showCashAccountType}
                  onChange={(e) =>
                    handleLocalShowTypeChange(
                      member._id,
                      e.target.value as ShowCashAccountType,
                    )
                  }
                  disabled={loading}
                  className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value={ShowCashAccountType.Self}>본인만 (Self)</option>
                  <option value={ShowCashAccountType.All}>전체 (All)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">계좌 노출 (accountExpose)</span>
              <select
                value={
                  local.accountExpose === undefined
                    ? 'inherit'
                    : local.accountExpose
                    ? 'true'
                    : 'false'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  const next = val === 'inherit' ? undefined : val === 'true';
                  setLocalState((prev) => {
                    const existing = prev[member._id];
                    if (!existing) return prev;
                    return {
                      ...prev,
                      [member._id]: { ...existing, accountExpose: next },
                    };
                  });
                }}
                disabled={loading || !local.bugoId}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="inherit">fevent 설정 따라감</option>
                <option value="true">켜기</option>
                <option value="false">끄기</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <div>
                <label className="text-xs font-medium text-gray-500">
                  공유 상단 텍스트 (topString)
                </label>
                <input
                  type="text"
                  value={local.shareDisplayCustom.topString}
                  onChange={(e) =>
                    setLocalState((prev) => {
                      const existing = prev[member._id];
                      if (!existing) return prev;
                      return {
                        ...prev,
                        [member._id]: {
                          ...existing,
                          shareDisplayCustom: {
                            ...existing.shareDisplayCustom,
                            topString: e.target.value,
                          },
                        },
                      };
                    })
                  }
                  placeholder={`${member.shortName} ${member.reverseType}상`}
                  disabled={loading}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">
                  공유 하단 텍스트 (bottomString)
                </label>
                <input
                  type="text"
                  value={local.shareDisplayCustom.bottomString}
                  onChange={(e) =>
                    setLocalState((prev) => {
                      const existing = prev[member._id];
                      if (!existing) return prev;
                      return {
                        ...prev,
                        [member._id]: {
                          ...existing,
                          shareDisplayCustom: {
                            ...existing.shareDisplayCustom,
                            bottomString: e.target.value,
                          },
                        },
                      };
                    })
                  }
                  placeholder={`${member.relationType} ${member.shortName} 배상`}
                  disabled={loading}
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            {member.cashAccount && typeof member.cashAccount === 'object' && (
              <div className="flex items-center justify-between rounded bg-white px-3 py-2 text-sm">
                <span className="text-gray-600">
                  {member.cashAccount.bank} {member.cashAccount.account} (
                  {member.cashAccount.name})
                </span>
                <span className="text-xs text-blue-500">본인 계좌</span>
              </div>
            )}

            {availableAccounts.length > 0 && (
              <div className="space-y-1 pt-1">
                <span className="text-xs font-medium text-gray-500">
                  추가 계좌 (subCashAccounts)
                </span>
                {availableAccounts.map((account) => {
                  const isLinked = local.subCashAccountIds.includes(account._id);
                  const ownerMember = memberList.find(
                    (m) =>
                      m._id !== member._id &&
                      m.cashAccount &&
                      typeof m.cashAccount === 'object' &&
                      m.cashAccount._id === account._id,
                  );
                  return (
                    <div
                      key={account._id}
                      className="flex items-center justify-between rounded bg-white px-3 py-1.5 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isLinked}
                          onChange={() =>
                            handleLocalToggleSub(member._id, account._id, !isLinked)
                          }
                          disabled={loading}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-gray-600">
                          {account.bank} {account.account} ({account.name})
                        </span>
                        {ownerMember && (
                          <span className="text-xs text-gray-400">
                            - {ownerMember.shortName}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {feventCashAccounts.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-bold text-gray-800">
            부의금 계좌 목록 (fevent.cashAccounts)
          </h4>
          {feventCashAccounts.map((account) => {
            const ownerMember = memberList.find(
              (m) =>
                m.cashAccount &&
                typeof m.cashAccount === 'object' &&
                m.cashAccount._id === account._id,
            );
            return (
              <EditableCashAccountRow
                key={account._id}
                account={account}
                ownerMember={ownerMember}
                bugoRole={bugoRole}
                onDelete={() => handleDeleteCashAccount(account._id)}
                onRefresh={onRefresh}
                disabled={loading}
                notificationInstance={notificationInstance}
              />
            );
          })}
        </div>
      )}

      {memberList.length === 0 && (
        <p className="text-sm text-gray-400">등록된 멤버가 없습니다.</p>
      )}

      {saveButton}
    </div>
  );
};

const EditableCashAccountRow: FC<{
  account: CashAccount;
  ownerMember?: Member<string, Bugo, CashAccount>;
  bugoRole: () => LowerRole;
  onDelete: () => void;
  onRefresh: () => Promise<void>;
  disabled: boolean;
  notificationInstance: NotificationInstance | null;
}> = ({
  account,
  ownerMember,
  bugoRole,
  onDelete,
  onRefresh,
  disabled,
  notificationInstance,
}) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(account.name);
  const [saving, setSaving] = useState(false);

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === account.name) {
      setEditing(false);
      setEditName(account.name);
      return;
    }
    setSaving(true);
    try {
      await updateCashAccount({ _id: account._id, name: trimmed }, bugoRole());
      await onRefresh();
      notificateSuccess(notificationInstance, '예금주명 수정 완료');
      setEditing(false);
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '예금주명 수정 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-md border border-gray-100 bg-white px-3 py-2">
      <div className="flex items-center gap-1 text-sm">
        <span className="font-medium text-gray-800">{account.bank}</span>
        <span className="ml-1 text-gray-600">{account.account}</span>
        {editing ? (
          <span className="ml-1 flex items-center gap-1">
            <span className="text-gray-400">(</span>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') {
                  setEditing(false);
                  setEditName(account.name);
                }
              }}
              autoFocus
              disabled={saving}
              className="w-24 rounded border border-blue-400 px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-gray-400">)</span>
            <button
              type="button"
              onClick={handleSaveName}
              disabled={saving}
              className="rounded bg-blue-500 px-1.5 py-0.5 text-xs text-white hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? '...' : '확인'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setEditName(account.name);
              }}
              disabled={saving}
              className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              취소
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={disabled}
            className="ml-1 text-gray-500 hover:text-blue-600 hover:underline disabled:opacity-50"
          >
            ({account.name})
          </button>
        )}
        {ownerMember && (
          <span className="ml-1 text-xs text-blue-500">- {ownerMember.shortName}</span>
        )}
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled || saving}
        className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
      >
        삭제
      </button>
    </div>
  );
};

const ImageUploadField: FC<{
  preview: string;
  clearable?: boolean;
  accept?: string;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  disabled?: boolean;
}> = ({ preview, clearable, accept = 'image/*', onFileSelect, onClear, disabled }) => (
  <div className="mt-1">
    <div className="flex items-center gap-2">
      <label
        className={`rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 ${
          disabled
            ? 'cursor-not-allowed bg-gray-100'
            : 'cursor-pointer bg-gray-50 hover:bg-gray-100'
        }`}
      >
        파일 선택
        <input
          type="file"
          accept={accept}
          onChange={onFileSelect}
          disabled={disabled}
          className="hidden"
        />
      </label>
      {(clearable ?? Boolean(preview)) && !disabled && (
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-red-500 hover:text-red-700"
        >
          삭제
        </button>
      )}
    </div>
    {preview && (
      <div className="mt-2 w-1/2">
        <img src={preview} alt="미리보기" className="rounded-md border object-cover" />
      </div>
    )}
  </div>
);
